import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RoleService, configService } from 'Services';
import Overview from 'Components/Overview';
import { handleOnClickEvent } from 'Utils';
import GlobalRulesOverviewModal from 'Components/GlobalRulesOverview/GlobalRulesOverviewModal/GlobalRulesOverviewModal';
import { GlobalRulesOverviewOverallSchema } from '@brighterion/types/overview';
import { Icon } from '@brighterion/br-ui';

import styles from './GlobalRulesOverviewOverall.css';

type GenOverview = { id: string; label: string; data: string | number };
type GlobalRulesOverviewProps = {
    data: GlobalRulesData;
    readOnly?: boolean;
};

export const genOverview = (apiData: GlobalRulesData, t: (s: String) => string): GenOverview[] => [
    {
        id: 'rules',
        label: t('Rules'),
        data: apiData?.rules.length
    },
    {
        id: 'active',
        label: t('Active'),
        data: apiData?.rules?.filter((o) => o.parameters.active).length
    },
    {
        id: 'modified',
        label: t('Modified'),
        data: apiData?.rules?.filter((r) => r.modifiedFlag === 'modified').length
    },
    {
        id: 'description',
        label: t('Description'),
        data: apiData?.config?.description || ''
    }
];

const GlobalRulesOverview = ({ data, readOnly }: GlobalRulesOverviewProps) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const hasEditOverviewAccess = RoleService.hasEditRuleOrBulkUploadAccess && !readOnly;
    const overallThreshold =
        data.config?.parameters?.overallThreshold || configService.constants.maxGlobalAggregateThreshold;
    const overview = genOverview(data, t);
    const threshold = [
        {
            label: t('Aggregate Scoring Threshold'),
            data: (
                <div className={styles.thresholdStatement}>
                    <Icon name='accept' size='md' className={styles.overallIcon} aria-label='accept' />
                    <span className={styles.thresholdConclusion}>{`Accept: score <= ${overallThreshold}`}</span>
                </div>
            )
        }
    ];

    const thresholdData: GlobalRulesOverviewOverallSchema = {
        description: data.config?.description || '',
        accept: overallThreshold || configService.constants.maxGlobalAggregateThreshold
    };

    const closeModal = () => {
        setIsOpen(false);
    };

    return (
        <>
            <Overview
                dataTestId='global-rules-overview'
                leftTitle='Global Rules Overview'
                onButtonLinkClick={hasEditOverviewAccess ? handleOnClickEvent(() => setIsOpen(true)) : undefined}
                leftData={overview}
                rightData={threshold}
            />
            {hasEditOverviewAccess && (
                <GlobalRulesOverviewModal closeModal={closeModal} isOpen={isOpen} thresholdData={thresholdData} />
            )}
        </>
    );
};

export default GlobalRulesOverview;
